import { hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import SavedDocIcon from "@/assets/icons/saved-doc.svg?react";
import UserUploadIcon from "@/assets/icons/user-upload.svg?react";
import { ConfiguratorStepProps, PrescriptionData, PrescriptionOptions } from "@/common/lib/types";
import Navigation from "@/components/lcp/components/Navigation";
import { useConfiguratorContext } from "@/components/lcp/context/Provider";
import SummaryReview from "@/components/lcp/summaryReview/SummaryReview";
import PrescriptionCard from "@/components/myAccount/prescriptions/PrescriptionCard";
import Button from "@/components/ui/button/Button";
import { getPreviousStep } from "@/utils/lensConfigurator";

interface IProps extends ConfiguratorStepProps {
  stepOptions: PrescriptionOptions;
}

const SavedPrescription = ({
  locale,
  currencyCode,
  translations,
  handleStepData,
  handleJumpToStep,
  stepOptions: { data, step },
}: IProps) => {
  const { steps, loading } = useConfiguratorContext();
  const currentStep = lensConfigurationSteps.PRESCRIPTION;

  const handleSkip = async () => {
    startPageTransition(false);
    await handleStepData(currentStep, null, 1);
    endPageTransition();
  };

  const handleGoToUpload = async () => {
    startPageTransition(false);
    await handleStepData(currentStep, null);
    endPageTransition();
  };

  const handleGoBack = async () => {
    if (!handleJumpToStep || typeof handleJumpToStep !== "function") return;
    startPageTransition(false);
    await handleJumpToStep(getPreviousStep(currentStep, steps));
    endPageTransition();
  };

  const handleSelectPrescription = async (item: PrescriptionData) => {
    startPageTransition(false);
    await handleStepData(currentStep, item, 1);
    endPageTransition();
  };

  return (
    <div>
      <Navigation disabled={loading} onGoBack={handleGoBack} className="hidden md:flex px-4">
        <p>{translations?.lensConfigurator?.lens_config_save_prescription_section_title}</p>
      </Navigation>
      <p className="mb-7 text-[0.75rem]">
        <span className="mr-1 font-bold">
          {translations?.lensConfigurator?.lens_config_save_prescription_section_subtitle_bold_text}
        </span>
        <span>
          {translations?.lensConfigurator?.lens_config_save_prescription_section_subtitle}
        </span>
      </p>
      <section className="grid grid-cols-12 gap-8">
        {hasArrayElements(data) ? (
          data.map((prescription, i) => (
            <PrescriptionCard
              key={i}
              translations={translations}
              prescription={prescription}
              onSelect={() => handleSelectPrescription(prescription)}
            />
          ))
        ) : (
          <div className="col-span-full flex h-[300px] flex-col items-center justify-center gap-7">
            <SavedDocIcon />
            <h3>
              {translations?.lensConfigurator?.lens_config_empty_prescription_description ||
                "Looks like You have no saved prescriptions!"}
            </h3>
          </div>
        )}
      </section>
      {step === currentStep && (
        <div className="mt-10 flex flex-col items-center justify-center gap-6 sm:mt-8 sm:flex-row lg:justify-start">
          <Button
            variant="outlined"
            disabled={loading}
            onClick={handleGoToUpload}
            className="flex w-full items-center justify-center gap-2 sm:w-max"
          >
            <UserUploadIcon width={13} height={13} />
            <span>
              {hasArrayElements(data)
                ? translations?.lensConfigurator?.lens_config_upload_prescription_label
                : translations?.lensConfigurator
                  ?.lens_config_empty_prescription_page_upload_button_label}
            </span>
          </Button>
          <Button
            variant="link"
            disabled={loading}
            onClick={handleSkip}
            className="text-tertiary-6"
          >
            {translations?.lensConfigurator?.lens_config_skip_prescription_label}
          </Button>
        </div>
      )}
      <SummaryReview locale={locale} translations={translations} currencyCode={currencyCode} />
    </div>
  );
};

export default SavedPrescription;
